import { getMetricDropdownLabel } from '../../Utils/chart_utils';
import dayjs from 'dayjs';

// Helper function to calculate linear regression for trendline
function calculateLinearRegression(data) {
  const validData = data.map((value, index) => ({ x: index, y: value }))
                       .filter(point => point.y !== null && point.y !== undefined && !isNaN(point.y));
  
  if (validData.length < 2) return null;
  
  const n = validData.length;
  const sumX = validData.reduce((sum, point) => sum + point.x, 0);
  const sumY = validData.reduce((sum, point) => sum + point.y, 0);
  const sumXY = validData.reduce((sum, point) => sum + (point.x * point.y), 0);
  const sumXX = validData.reduce((sum, point) => sum + (point.x * point.x), 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  return { slope, intercept };
}

export function mapDataForLineHighcharts(statData, teamColors, stat, showTitle = true, showHamburger = true, forceTitle = "", showDataLabels = false, pageType = "team", showBodyWeightBaseline = false, showTrendlines = false) {

  const data = statData?.data || [];
  const optionsV2 = statData?.optionsV2 || [];
  if (!data) return;

  const chartData = data.map((dataPoint) => {
    let restructuredDataPoint = {
      date: dataPoint.date,
      disp_date: dayjs(dataPoint.date).format("MM-DD"),
    }
    optionsV2.forEach(option => {
      restructuredDataPoint[option.name] = dataPoint[option.name];
    });
    return restructuredDataPoint;
  });

  // Create a stable color mapping based on the original optionsV2 array
  // This ensures colors remain consistent regardless of series array modifications
  const originalSeriesColors = optionsV2.map((item, index) => ({
    name: item.name,
    color: teamColors[index % teamColors.length],
    index: index
  }));

  const series = optionsV2.map((item, index) => {
    return {
      id: `series-${index}`, // Add unique ID for linking
      name: item.name,
      type: 'line',
      yAxis: 0,
      data: chartData.map(innerItem => innerItem[item.name] || null),
      color: originalSeriesColors[index].color, // Use stable color mapping
      dataLabels: {
        enabled: showDataLabels,
        format: '{y}',
      }
    }
  });

  // Add trendlines if enabled
  if (showTrendlines) {
    optionsV2.forEach((item, index) => {
      const seriesData = chartData.map(innerItem => innerItem[item.name] || null);
      
      // Find valid (non-null) data points and their indices
      const validDataPoints = [];
      seriesData.forEach((value, dataIndex) => {
        if (value !== null && value !== undefined && !isNaN(value)) {
          validDataPoints.push({ value, index: dataIndex });
        }
      });
      
      // Skip trendline if we have fewer than 2 valid data points
      if (validDataPoints.length < 2) {
        return;
      }
      
      // Calculate regression using only valid data points
      const regression = calculateLinearRegression(seriesData);
      
      if (regression) {
        // Find the range of actual data (first and last valid data points)
        const firstDataIndex = validDataPoints[0].index;
        const lastDataIndex = validDataPoints[validDataPoints.length - 1].index;
        
        // Generate trendline data only for the interval with actual data
        const trendlineData = seriesData.map((_, dataIndex) => {
          // Only generate trendline values within the data range
          if (dataIndex >= firstDataIndex && dataIndex <= lastDataIndex) {
            return regression.slope * dataIndex + regression.intercept;
          }
          return null; // No trendline outside the data range
        });
        
        // Use the stable color mapping to ensure consistency
        const parentSeriesColor = originalSeriesColors[index].color;
        
        series.push({
          name: `${item.name} Trend`,
          type: 'line',
          yAxis: 0,
          data: trendlineData,
          color: parentSeriesColor, // Use the stable color reference
          dashStyle: 'Dash',
          lineWidth: 2,
          opacity: 0.7,
          enableMouseTracking: false,
          showInLegend: false,
          linkedTo: `series-${index}`, // Link to parent series
          marker: {
            enabled: false // Remove markers from trendlines
          },
          dataLabels: {
            enabled: false
          }
        });
      }
    });
  }

  let referenceLines = []
  // Add baseline series for any option that has a baseline value
  optionsV2.forEach((item, index) => {
    if ((item.baseline !== undefined) && showBodyWeightBaseline) {
      referenceLines.push({
        id: 'avg',
        dashStyle: 'Dash',
        color: 'black',
        value: Math.round(item.baseline),
        zIndex: 5,
        label: {
          text: `Body Weight Baseline: ${Math.round(item.baseline)}`,
          align: 'right',
          x: -10
        }
      })
    }
  });

  const categories = chartData.map(item => item.disp_date)
  let title
  if (forceTitle) {
    title = forceTitle
  } else {
    title = (showTitle ? getMetricDropdownLabel("ASDF", false) : '');
  }

  // Set legend position based on pageType
  const legendConfig = pageType === "profile"
    ? {
      enabled: true,
      align: 'center',
      verticalAlign: 'top',
      layout: 'horizontal'
    }
    : {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical'
    };

  const options = {
    chart: {
      type: 'line',
      events: {
        load: function() {
          // Reset Highcharts color counters to ensure consistent colors
          // This prevents color drift when charts are recreated (e.g., when toggling trendlines)
          this.colorCounter = 0;
          this.symbolCounter = 0;
        },
        redraw: function() {
          // Also reset on redraw to maintain consistency
          this.colorCounter = 0;
          this.symbolCounter = 0;
        }
      }
    },
    title: {
      text: title
    },
    xAxis: {
      categories: categories,
      labels: categories.length > 20
        ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
        : categories.length > 10
          ? {
            rotation: -45,
            overflow: 'justify',
            staggerLines: 1,
            step: 0
          }
          : {
            rotation: 0,
          },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      crosshair: true,
      gridLineWidth: 1,
      gridLineColor: '#e6e6e6'
    },
    yAxis: [
      {
        title: {
          text: null
        },
        labels: {
          format: '{value}'
        },
        startOnTick: true,
        endOnTick: true,
        plotLines: referenceLines
      },
      {
        title: {
          text: null
        },
        labels: {
          format: '{value}'
        },
        opposite: true,
        startOnTick: true,
        endOnTick: true
      },
    ],
    tooltip: {
      shared: true
    },
    legend: legendConfig,
    plotOptions: {
      connectNulls: true,
      series: {
        // This connects points even if some data is null
        connectNulls: true,
        events: {
          legendItemClick: function() {
            // Allow default behavior (show/hide series)
            // Highcharts will automatically rescale the y-axis
            return true;
          }
        }
      },
      column: {
        grouping: true,
        shadow: false,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          inside: true
        }
      }
    },
    series: series,
    exporting: {
      enabled: showHamburger
    }
  };

  return options;
}